import { useState, useEffect } from 'react';
import { MapPin, Phone, Gift, Home as HomeIcon, Search, Heart } from 'lucide-react';
import { useMembers, useHostingCounts } from '../hooks/useMembers';
import { AppShell } from '../components/layout/AppShell';
import { PageHeader } from '../components/layout/PageHeader';
import { Card } from '../components/ui/Card';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Profile } from '../types';
import { formatMonthDay } from '../lib/dates';

export function MembersPage() {
  const { members, loading: membersLoading } = useMembers();
  const hostingCounts = useHostingCounts();
  const [search, setSearch] = useState('');
  const [timedOut, setTimedOut] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setTimedOut(true), 6000);
    return () => clearTimeout(t);
  }, []);
  const loading = membersLoading && !timedOut;

  const filtered = members.filter((m) =>
    m.full_name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <AppShell>
      <PageHeader
        title="Members"
        subtitle={`${members.length} ${members.length === 1 ? 'person' : 'people'}`}
      />

      <div className="px-4 pt-3 pb-4 space-y-3">
        {/* Search */}
        <div className="relative">
          <Search
            size={15}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            placeholder="Search members…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm
              text-gray-800 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-warm-400
              focus:border-transparent transition-all shadow-sm"
          />
        </div>

        {loading ? (
          <LoadingSpinner />
        ) : filtered.length === 0 ? (
          <div className="text-center py-8 text-gray-400 dark:text-gray-600">
            {search ? 'No members match your search.' : 'No members yet.'}
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((member) => (
              <MemberCard
                key={member.id}
                member={member}
                hostingCount={hostingCounts[member.id] ?? 0}
              />
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}

function MemberCard({
  member,
  hostingCount,
}: {
  member: Profile;
  hostingCount: number;
}) {
  const initials = member.full_name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const mapsUrl = member.address
    ? `https://maps.google.com/?q=${encodeURIComponent(member.address)}`
    : null;

  return (
    <Card>
      <div className="flex items-start gap-3">
        {/* Avatar — photo if available, else initials */}
        {member.avatar_url ? (
          <img
            src={member.avatar_url}
            alt={member.full_name}
            className="w-11 h-11 rounded-full object-cover flex-shrink-0 border border-warm-100 dark:border-gray-700"
          />
        ) : (
          <div className="w-11 h-11 rounded-full bg-warm-100 dark:bg-warm-900 flex items-center justify-center flex-shrink-0">
            <span className="text-sm font-bold text-warm-600 dark:text-warm-300">{initials}</span>
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm">{member.full_name}</h3>
            {hostingCount > 0 && (
              <div className="flex items-center gap-1 text-xs text-warm-500 bg-warm-50 dark:bg-warm-950 rounded-full px-2 py-0.5 flex-shrink-0">
                <HomeIcon size={11} />
                <span>Hosted {hostingCount}×</span>
              </div>
            )}
          </div>

          <div className="mt-1.5 space-y-1">
            {member.birthday && (
              <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                <Gift size={12} className="text-pink-400" />
                <span>{formatMonthDay(member.birthday)}</span>
              </div>
            )}

            {member.spouse_name && (
              <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                <Heart size={12} className="text-rose-400" />
                <span>{member.spouse_name}</span>
              </div>
            )}

            {member.address && mapsUrl && (
              <a
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-1.5 text-xs text-warm-600 dark:text-warm-400 hover:text-warm-700 dark:hover:text-warm-300 group"
              >
                <MapPin size={12} className="flex-shrink-0 mt-0.5 group-hover:scale-110 transition-transform" />
                <span className="truncate">{member.address}</span>
              </a>
            )}

            {member.phone && (
              <a
                href={`tel:${member.phone}`}
                className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 hover:text-warm-600"
              >
                <Phone size={12} />
                <span>{member.phone}</span>
              </a>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}
